import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { notify } from "../../components/ui/notify";
import {
  Select,
  Spin,
  Button,
  Upload,
} from "antd";
import { InboxOutlined, DeleteOutlined, FileOutlined } from "@ant-design/icons";
import { HappyProvider } from "@ant-design/happy-work-theme";

import {
  actionJuntaGetByIdRedux,
  actionJuntaCreate,
  actionJuntaUpdateById,
  actionJuntaDeleteDocumento,
} from "../../redux/actions/junta/junta";
import { PATH } from "../../redux/utils";
import { actionAutoridadesJuntaGet } from "../../redux/actions/autoridades/autoridades";
import { actionEmpresasGet } from "../../redux/actions/empresas/empresas";
import { actionAbogadosGet } from "../../redux/actions/abogados/abogados";
import useLaboralCatalogos from "./materias/laboral/useLaboralCatalogos";
import "../../components/forms/tribunal/FormTribunal.css";
import ScrollDateInput from "../../components/calendar/ScrollDateInput";

const collapseSpaces = (s) => String(s || "").replace(/\s+/g, " ").trim();

const COMPETENCIA_OPTS = [
  { label: "Local", value: "Local" },
  { label: "Federal", value: "Federal" },
];

const TIPO_PROCEDIMIENTO_OPTS = [
  { label: "Ordinario", value: "Ordinario" },
  { label: "Especial (art. 892)", value: "Especial (art. 892)" },
  { label: "Paraprocesal o voluntario (art. 982)", value: "Paraprocesal o voluntario (art. 982)" },
  { label: "Colectivo", value: "Colectivo" },
];

const REGIMEN_REFORMA_2012_OPTS = [
  { label: "Antes de la reforma 2012", value: "antes" },
  { label: "Después de la reforma 2012", value: "despues" },
];

const CARACTER_DEMANDADO_OPTS = [
  "Patrón solidario",
  "Socio o accionista",
  "Representante legal",
  "Administrador o apoderado",
  "Beneficiario del servicio",
  "Quien resulte responsable",
];

const TIPO_ASESORIA_OPTS = ["Particular", "PROFEDET", "Sindicato", "Sin asesor"];

const ETAPA_PROCESAL_OPTS = [
  "Demanda presentada — pendiente de radicación",
  "Radicada — pendiente de emplazamiento",
  "Emplazada — audiencia trifásica señalada",
  "Audiencia de conciliación, demanda y excepciones celebrada",
  "Ofrecimiento y admisión de pruebas",
  "Desahogo de pruebas",
  "Alegatos",
  "Instrucción cerrada",
  "Proyecto de laudo / dictamen",
  "Laudo dictado",
  "Amparo directo",
  "Ejecución de laudo",
  "Convenio / desistimiento / caducidad",
  "Concluido y archivado",
];

const ACCION_INTENTADA_OPTS = [
  "Despido injustificado — con reinstalación",
  "Despido injustificado — con indemnización constitucional",
  "Rescisión por causa imputable al patrón (art. 51)",
  "Cumplimiento de contrato / pago de prestaciones",
  "Derecho de preferencia y/o ascenso",
  "Titularidad de contrato colectivo (art. 389)",
  "Designación de beneficiarios (arts. 501-503)",
  "Riesgo de trabajo / incapacidad (arts. 487 y ss.)",
  "Conflicto colectivo de naturaleza económica",
  "Procedimiento paraprocesal o voluntario (art. 982)",
];

const DILIGENCIA_OPTS = [
  "Audiencia de conciliación, demanda y excepciones (art. 873)",
  "Audiencia de ofrecimiento y admisión de pruebas (art. 880)",
  "Audiencia de desahogo de pruebas (art. 883)",
  "Comparecencia para absolución de posiciones (arts. 786 a 790)",
  "Desahogo de testimonial (art. 813)",
  "Diligencia de inspección (arts. 827 a 829)",
  "Ratificación de contenido y firma",
  "Audiencia incidental (art. 763)",
  "Alegatos (art. 885)",
  "Audiencia de discusión y votación del laudo (arts. 888 y 889)",
  "Diligencia de reinstalación",
  "Ratificación de convenio",
  "Diligencia de ejecución de laudo (art. 940)",
  "Otra diligencia",
];

// Fecha de entrada en vigor de la reforma laboral (Juntas -> Tribunales) por
// entidad — mismos valores que el mockup de referencia. Solo informativo: no
// se guarda, solo alerta si la demanda se presentó ya bajo el nuevo sistema.
const CORTE_REGIMEN = {
  "Baja California": "2021-11-03",
  "Sonora": "2022-10-03",
};

const genRowId = () => `row_${Date.now()}_${Math.random().toString(16).slice(2)}`;
const safeArr = (v) => (Array.isArray(v) ? v : []);
const safeObj = (v) => (v && typeof v === "object" ? v : {});

// Mismos catálogos/valores que en Tribunal (PruebasSection es un componente
// compartido; no se duplica, solo se reutiliza tal cual con estos props).
const MEDIOS_OPTS = [
  { label: "WhatsApp", value: "whatsapp" },
  { label: "Correo", value: "correo" },
  { label: "SMS", value: "sms" },
  { label: "Redes sociales", value: "redes_sociales" },
  { label: "Audios", value: "audios" },
  { label: "Videos", value: "videos" },
  { label: "Fotografías", value: "fotos" },
  { label: "Capturas", value: "capturas" },
  { label: "Plataformas", value: "plataformas" },
  { label: "Archivos digitales", value: "archivos" },
];

const isoToYMD = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

// Helpers de empresas/razones sociales copiados 1:1 de FormTribunal.jsx —
// PruebasSection (Confesional Demandada) necesita `empresasIndex` con esta
// misma forma: Map<nombreEmpresa, { empresaIds:Set, razones:[{label,value,empresa_id}] }>
const pickCompanyName = (it) =>
  it?.nombre ?? it?.nombre_cliente ?? it?.empresa ?? it?.name ?? it?.razon_social ?? "";
const pickCompanyId = (it) => it?.id_empresa ?? it?.id ?? null;

const buildRSOption = ({ label, value, empresa_id }) => ({
  label: collapseSpaces(label || ""),
  value: value ?? collapseSpaces(label || ""),
  empresa_id: empresa_id ?? null,
});

const normLabelLocal = (txt) => collapseSpaces(txt || "").trim().toLowerCase();

const dedupRazones = (arr = []) => {
  const map = new Map();
  for (const r of arr) {
    const key = `${normLabelLocal(r.label)}|${r.empresa_id ?? "null"}`;
    const existing = map.get(key);
    if (!existing) map.set(key, r);
    else if (existing.value == null && r.value != null) map.set(key, r);
  }
  return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, "es"));
};

const buildEmpresasIndex = (items = []) => {
  const byName = new Map();
  if (!Array.isArray(items)) return byName;

  items.forEach((it) => {
    const name = collapseSpaces(pickCompanyName(it));
    if (!name) return;

    const idEmpresa = pickCompanyId(it);
    if (!byName.has(name)) byName.set(name, { empresaIds: new Set(), razones: [] });
    const bucket = byName.get(name);

    if (idEmpresa != null) bucket.empresaIds.add(idEmpresa);

    const rsArr = Array.isArray(it?.razones_sociales) ? it.razones_sociales : [];
    rsArr.forEach((rs) => {
      const label = rs?.nombre ?? rs?.razon_social ?? rs?.name ?? "";
      const value = rs?.id_empresa_razon_social ?? rs?.id ?? null;
      const empresa_id = rs?.id_empresa ?? idEmpresa ?? null;
      if (!label) return;
      bucket.razones.push(buildRSOption({ label, value, empresa_id }));
    });

    const plainRS =
      it?.razon_social && String(it.razon_social).trim() !== "" ? String(it.razon_social) : null;
    if (plainRS) bucket.razones.push(buildRSOption({ label: plainRS, value: null, empresa_id: idEmpresa }));
  });

  for (const [name, bucket] of byName.entries()) {
    bucket.razones = dedupRazones(bucket.razones);
    byName.set(name, bucket);
  }
  return byName;
};

const makeNewSelectValue = (label) => {
  const clean = collapseSpaces(label);
  const stamp = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
  return `new:${clean}:${stamp}`;
};

// Mapea la respuesta del backend (detalle.pruebas_documentacion) a la forma
// que consume PruebasSection — mismo mapeo que backendToInitialValues en
// crearTribunalPage.jsx.
const backendPruebasToForm = (pd) => {
  pd = pd || {};
  const detalleActora = pd?.detalle?.actora || {};
  const checklistActora = pd?.checklist?.actora || {};

  const confesionalArr = Array.isArray(detalleActora?.confesional)
    ? detalleActora.confesional.map((item) => ({
        id: item?.id || genRowId(),
        razon_social: item?.razon_social || undefined,
      }))
    : [];

  const rawConfesionalPropios = detalleActora?.confesional_hechos_propios;
  const confesionalPropiosArr = (
    Array.isArray(rawConfesionalPropios)
      ? rawConfesionalPropios
      : Array.isArray(rawConfesionalPropios?.registros)
      ? rawConfesionalPropios.registros
      : []
  ).map((item) => ({
    id: item?.id || genRowId(),
    tipo_prueba: item?.tipo_prueba || "",
    hechos_propios: item?.hechos_propios || "",
  }));

  const testimonialTestigos = Array.isArray(detalleActora?.testimonial?.testigos)
    ? detalleActora.testimonial.testigos.map((t) => ({
        id: t?.id || genRowId(),
        nombre: t?.nombre || "",
        relacion: t?.relacion || "",
        contacto: t?.contacto || "",
      }))
    : [];

  const docPublicaDocs = Array.isArray(detalleActora?.documental_publica?.documentos)
    ? detalleActora.documental_publica.documentos.map((doc) => ({
        id: doc?.id || genRowId(),
        nombre: doc?.nombre || "",
        descripcion: doc?.descripcion || "",
        fecha: isoToYMD(doc?.fecha),
      }))
    : [];

  const docPrivadaDocs = Array.isArray(detalleActora?.documental_privada?.documentos)
    ? detalleActora.documental_privada.documentos.map((doc) => ({
        id: doc?.id || genRowId(),
        nombre: doc?.nombre || "",
        descripcion: doc?.descripcion || "",
        fecha: isoToYMD(doc?.fecha),
      }))
    : [];

  const informeAutoridadDocs = Array.isArray(detalleActora?.informe_autoridad?.documentos)
    ? detalleActora.informe_autoridad.documentos.map((doc) => ({
        id: doc?.id || genRowId(),
        nombre: doc?.nombre || "",
        descripcion: doc?.descripcion || "",
        fecha: isoToYMD(doc?.fecha),
      }))
    : [];

  const rawPericial = detalleActora?.pericial;
  const pericialItems = Array.isArray(rawPericial)
    ? rawPericial
    : Array.isArray(rawPericial?.items)
    ? rawPericial.items
    : Array.isArray(rawPericial?.registros)
    ? rawPericial.registros
    : [];
  const pericialArr = pericialItems.map((item) => ({
    id: item?.id || genRowId(),
    tipo_pericial: Array.isArray(item?.tipo_pericial)
      ? item.tipo_pericial
      : item?.tipo_pericial
      ? [item.tipo_pericial]
      : Array.isArray(item?.tipo)
      ? item.tipo
      : item?.tipo
      ? [item.tipo]
      : [],
    nombre_perito: item?.nombre_perito || item?.perito || "",
    objeto_prueba: item?.objeto_prueba || item?.objeto || "",
  }));

  const rawInspeccion = detalleActora?.inspeccion_ocular;
  const inspeccionItems = Array.isArray(rawInspeccion)
    ? rawInspeccion
    : Array.isArray(rawInspeccion?.items)
    ? rawInspeccion.items
    : Array.isArray(rawInspeccion?.registros)
    ? rawInspeccion.registros
    : [];
  const inspeccionArr = inspeccionItems.map((item) => ({
    id: item?.id || genRowId(),
    tipo_inspeccion_ocular: Array.isArray(item?.tipo_inspeccion_ocular)
      ? item.tipo_inspeccion_ocular
      : item?.tipo_inspeccion_ocular
      ? [item.tipo_inspeccion_ocular]
      : Array.isArray(item?.tipo)
      ? item.tipo
      : item?.tipo
      ? [item.tipo]
      : [],
    nombre_inspeccion_ocular: item?.nombre_inspeccion_ocular || item?.nombre || "",
    objeto_prueba: item?.objeto_prueba || item?.objeto || "",
  }));

  const mediosElectronicosTipos = Array.isArray(detalleActora?.medios_electronicos?.tipos)
    ? detalleActora.medios_electronicos.tipos
    : [];

  const inferredChecklist = {
    ...checklistActora,
    confesional: checklistActora?.confesional ?? confesionalArr.length > 0,
    confesional_hechos_propios: checklistActora?.confesional_hechos_propios ?? confesionalPropiosArr.length > 0,
    confesional_expresa: checklistActora?.confesional_expresa ?? false,
    testimonial: checklistActora?.testimonial ?? testimonialTestigos.length > 0,
    documental_publica: checklistActora?.documental_publica ?? docPublicaDocs.length > 0,
    documental_privada: checklistActora?.documental_privada ?? docPrivadaDocs.length > 0,
    informe_autoridad: checklistActora?.informe_autoridad ?? informeAutoridadDocs.length > 0,
    pericial: checklistActora?.pericial ?? pericialArr.length > 0,
    inspeccion_ocular: checklistActora?.inspeccion_ocular ?? inspeccionArr.length > 0,
    medios_electronicos: checklistActora?.medios_electronicos ?? mediosElectronicosTipos.length > 0,
    instrumental_actuaciones: checklistActora?.instrumental_actuaciones ?? true,
    presuncional_legal_humana: checklistActora?.presuncional_legal_humana ?? true,
  };

  return {
    pruebas_checklist: { actora: inferredChecklist },
    pruebas_detalle: {
      actora: {
        confesional: confesionalArr,
        confesional_hechos_propios: confesionalPropiosArr,
        testimonial: {
          tipo: Array.isArray(detalleActora?.testimonial?.tipo) ? detalleActora.testimonial.tipo : [],
          draft_nombre: "",
          testigos: testimonialTestigos,
        },
        documental_publica: { documentos: docPublicaDocs },
        documental_privada: { documentos: docPrivadaDocs },
        informe_autoridad: { documentos: informeAutoridadDocs },
        pericial: pericialArr,
        inspeccion_ocular: inspeccionArr,
        medios_electronicos: {
          tipos: mediosElectronicosTipos,
          descripcion: detalleActora?.medios_electronicos?.descripcion || "",
        },
      },
    },
  };
};

const coerceEmpresas = (slice) => {
  if (Array.isArray(slice)) return slice;
  if (Array.isArray(slice?.items)) return slice.items;
  if (Array.isArray(slice?.data)) return slice.data;
  if (Array.isArray(slice?.data?.items)) return slice.data.items;
  return [];
};

const coerceAutoridades = (slice) => {
  const d = slice?.data ?? slice;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.items)) return d.items;
  if (Array.isArray(d?.data)) return d.data;
  return [];
};

export default function CrearJuntaConciliacionPage() {
  const { idJunta } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isEdit = Boolean(idJunta);

  const detallesState = useSelector((s) => s?.junta?.detalles);
  const detalle = detallesState?.detalle ?? detallesState ?? null;
  const autoridadesSlice = useSelector((s) => s.autoridades || {});
  const empresasSlice = useSelector((s) => s.empresas || {});
  const abogadosSlice = useSelector((s) => s.abogados || {});

  const [selectedEstadoId, setSelectedEstadoId] = useState(null);
  const [selectedCiudadId, setSelectedCiudadId] = useState(null);
  const [selectedAutoridadId, setSelectedAutoridadId] = useState(null);

  const { estadosOptions, ciudadesOptions, ciudadesById } = useLaboralCatalogos(
    selectedEstadoId,
    selectedCiudadId,
    { modulo: "junta" }
  );

  const autoridadesRaw = useMemo(() => coerceAutoridades(autoridadesSlice), [autoridadesSlice]);

  const autoridadOptions = useMemo(() => {
    if (!selectedCiudadId) return [];
    return autoridadesRaw
      .filter((a) => String(a.id_ciudad || "") === String(selectedCiudadId))
      .map((a) => ({ label: collapseSpaces(a.nombre || ""), value: a.id_autoridad ?? a.id }))
      .filter((o) => o.label);
  }, [autoridadesRaw, selectedCiudadId]);

  const ciudadesIdsConAutoridad = useMemo(() => {
    const s = new Set();
    autoridadesRaw.forEach((a) => { if (a.id_ciudad != null) s.add(Number(a.id_ciudad)); });
    return s;
  }, [autoridadesRaw]);

  const ciudadesOptionsFinal = useMemo(() => {
    if (!selectedEstadoId) return [];
    return (ciudadesOptions || []).filter((o) => ciudadesIdsConAutoridad.has(Number(o.value)));
  }, [ciudadesOptions, ciudadesIdsConAutoridad, selectedEstadoId]);

  const abogadosItems = useMemo(() => coerceEmpresas(abogadosSlice), [abogadosSlice]);
  const abogadosOptions = useMemo(
    () =>
      abogadosItems
        .map((a) => ({ label: collapseSpaces(a?.nombre || ""), value: a?.id_abogado ?? a?.id }))
        .filter((o) => o.label)
        .sort((a, b) => a.label.localeCompare(b.label, "es")),
    [abogadosItems]
  );

  const empresasItems = useMemo(() => coerceEmpresas(empresasSlice), [empresasSlice]);
  const empresasIndex = useMemo(() => buildEmpresasIndex(empresasItems), [empresasItems]);
  const empresaNombreOptions = useMemo(() => {
    const names = Array.from(empresasIndex.keys());
    return names.map((n) => ({ label: n, value: n })).sort((a, b) => a.label.localeCompare(b.label, "es"));
  }, [empresasIndex]);

  const todasRazonesOptions = useMemo(() => {
    const seen = new Set();
    const all = [];
    empresasItems.forEach((emp) => {
      const rsArr = Array.isArray(emp.razones_sociales) ? emp.razones_sociales : [];
      rsArr.forEach((rs) => {
        const label = collapseSpaces(rs?.nombre || rs?.razon_social || "");
        const value = String(rs?.id ?? rs?.id_empresa_razon_social ?? "");
        if (label && value && !seen.has(value)) {
          seen.add(value);
          all.push({ label, value });
        }
      });
    });
    return all.sort((a, b) => a.label.localeCompare(b.label));
  }, [empresasItems]);

  const [form, setFormState] = useState({
    expediente: "",
    actores: [""],
    demandados: [{ id_razon_social: "", caracter: "" }],
    corresponsal_nombre: "",
    abogado: "",
    competencia: "",
    tipo_procedimiento: "",
    regimen_reforma_2012: "",
    expedientes_relacionados: "",
    tipo_asesoria_actora: "",
    id_abogado_responsable: "",
    etapa_procesal_inicial: "",
    accion_intentada: "",
    fecha_presentacion_demanda: "",
    fecha_recepcion: "",
    fecha_emplazamiento: "",
    fecha_audiencia_fecha: "",
    fecha_audiencia_hora: "",
    fecha_ultima_actuacion: "",
    fecha_proxima_diligencia: "",
    descripcion_proxima_diligencia: "",
    comentarios: "",
    pruebas_checklist: { actora: {} },
    pruebas_detalle: { actora: {} },
  });
  const [catalogOverrides, setCatalogOverrides] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [errors, setErrors] = useState({});

  // Documentos: archivos nuevos pendientes de subir (se envían junto con el
  // resto del expediente al crear/guardar) + documentos ya existentes (edit).
  const [pendingDocs, setPendingDocs] = useState([]);
  const [existingDocs, setExistingDocs] = useState([]);
  const [deletingDocId, setDeletingDocId] = useState(null);

  const setField = (name, value) => {
    setFormState((p) => ({ ...p, [name]: value }));
    setErrors((e) => { const c = { ...e }; delete c[name]; return c; });
  };

  // =========================
  // PARTES: actores (repetible) y demandados (razón social + carácter,
  // repetible) — mismo patrón "+ Agregar otro" del mockup.
  // =========================
  const setActorAt = (idx, value) => {
    setFormState((p) => {
      const next = [...safeArr(p.actores)];
      next[idx] = value;
      return { ...p, actores: next };
    });
    setErrors((e) => { const c = { ...e }; delete c.actores; return c; });
  };
  const addActor = () => setFormState((p) => ({ ...p, actores: [...safeArr(p.actores), ""] }));
  const removeActor = (idx) =>
    setFormState((p) => {
      const next = safeArr(p.actores).filter((_, i) => i !== idx);
      return { ...p, actores: next.length ? next : [""] };
    });

  const setDemandadoAt = (idx, patch) => {
    setFormState((p) => {
      const next = safeArr(p.demandados).map((d, i) => (i === idx ? { ...d, ...patch } : d));
      return { ...p, demandados: next };
    });
    setErrors((e) => { const c = { ...e }; delete c.demandados; return c; });
  };
  const addDemandado = () =>
    setFormState((p) => ({
      ...p,
      demandados: [...safeArr(p.demandados), { id_razon_social: "", caracter: "" }],
    }));
  const removeDemandado = (idx) =>
    setFormState((p) => {
      const next = safeArr(p.demandados).filter((_, i) => i !== idx);
      return { ...p, demandados: next.length ? next : [{ id_razon_social: "", caracter: "" }] };
    });

  // =========================
  // PRUEBAS: mismos helpers que FormTribunal.jsx (componente PruebasSection
  // compartido — no se toca, solo se le pasan estos props tal cual).
  // =========================
  const setPruebasDetalle = (key, valueObj) => {
    setFormState((p) => ({
      ...p,
      pruebas_detalle: {
        ...(p.pruebas_detalle || {}),
        actora: {
          ...((p.pruebas_detalle && p.pruebas_detalle.actora) || {}),
          [key]: valueObj,
        },
      },
    }));
  };

  const toggleChecklistActora = (key) => {
    setFormState((p) => {
      const prev = safeObj(p.pruebas_checklist);
      const act = safeObj(prev.actora);
      return {
        ...p,
        pruebas_checklist: {
          ...prev,
          actora: { ...act, [key]: !act[key] },
        },
      };
    });
  };

  const checklistActivo = useMemo(() => {
    const act = safeObj(form?.pruebas_checklist?.actora);
    return {
      confesional: !!act.confesional,
      confesional_hechos_propios: !!act.confesional_hechos_propios,
      testimonial: !!act.testimonial,
      medios_electronicos: !!act.medios_electronicos,
      documental_publica: !!act.documental_publica,
      documental_privada: !!act.documental_privada,
      informe_autoridad: !!act.informe_autoridad,
      documental: !!act.documental,
      pericial: !!act.pericial,
      inspeccion_ocular: !!act.inspeccion_ocular,
    };
  }, [form?.pruebas_checklist?.actora]);

  const removeTestigo = (id) => {
    setFormState((p) => {
      const pd = safeObj(p.pruebas_detalle);
      const act = safeObj(pd.actora);
      const t = safeObj(act.testimonial);

      const prevList = safeArr(t.testigos);
      const nextList = prevList.filter((x) => String(x.id) !== String(id));

      return {
        ...p,
        pruebas_detalle: {
          ...pd,
          actora: { ...act, testimonial: { ...t, testigos: nextList } },
        },
      };
    });
  };

  const setMediosElectronicosTipos = (arr) => {
    const next = safeArr(arr);
    setPruebasDetalle("medios_electronicos", {
      ...(safeObj(form?.pruebas_detalle?.actora?.medios_electronicos) || {}),
      tipos: next,
    });
  };

  const toggleMedioElectronico = (value) => {
    const cur = safeArr(form?.pruebas_detalle?.actora?.medios_electronicos?.tipos);
    const exists = cur.includes(value);
    const next = exists ? cur.filter((x) => x !== value) : [...cur, value];
    setMediosElectronicosTipos(next);
  };

  useEffect(() => {
    dispatch(actionAutoridadesJuntaGet());
    dispatch(actionEmpresasGet());
    dispatch(actionAbogadosGet());
  }, [dispatch]);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        setLoadingEdit(true);
        await dispatch(actionJuntaGetByIdRedux(idJunta));
      } catch {
        notify.error({ message: "No se pudo cargar el expediente" });
      } finally {
        setLoadingEdit(false);
      }
    })();
  }, [dispatch, isEdit, idJunta]);

  useEffect(() => {
    if (!isEdit || !detalle) return;

    const pruebasForm = backendPruebasToForm(detalle.pruebas_documentacion);

    const actoresIniciales = collapseSpaces(detalle.nombre_actora || "")
      .split(" - ")
      .map((s) => s.trim())
      .filter(Boolean);

    const demandadosIniciales = (detalle.razones_sociales || [])
      .filter((rs) => (rs.id_razon_social ?? rs.id) != null)
      .map((rs) => ({
        id_razon_social: String(rs.id_razon_social ?? rs.id ?? ""),
        caracter: rs.caracter || "",
      }));

    setFormState((p) => ({
      ...p,
      expediente: detalle.expediente || "",
      actores: actoresIniciales.length ? actoresIniciales : [""],
      demandados: demandadosIniciales.length ? demandadosIniciales : [{ id_razon_social: "", caracter: "" }],
      corresponsal_nombre: detalle.corresponsal_nombre || "",
      abogado: detalle.abogado || "",
      competencia: detalle.competencia || "",
      tipo_procedimiento: detalle.tipo_procedimiento || "",
      regimen_reforma_2012: detalle.regimen_reforma_2012 || "",
      expedientes_relacionados: detalle.expedientes_relacionados || "",
      tipo_asesoria_actora: detalle.tipo_asesoria_actora || "",
      id_abogado_responsable: detalle.id_abogado_responsable ? String(detalle.id_abogado_responsable) : "",
      etapa_procesal_inicial: detalle.etapa_procesal_inicial || "",
      accion_intentada: detalle.accion_intentada || "",
      fecha_presentacion_demanda: isoToYMD(detalle.fecha_presentacion_demanda),
      fecha_recepcion: isoToYMD(detalle.fecha_recepcion),
      fecha_emplazamiento: isoToYMD(detalle.fecha_emplazamiento),
      fecha_audiencia_fecha: isoToYMD(detalle.fecha_audiencia),
      fecha_audiencia_hora: detalle.fecha_audiencia
        ? String(detalle.fecha_audiencia).replace("T", " ").slice(11, 16)
        : "",
      fecha_ultima_actuacion: isoToYMD(detalle.fecha_ultima_actuacion),
      fecha_proxima_diligencia: isoToYMD(detalle.fecha_proxima_diligencia),
      descripcion_proxima_diligencia: detalle.descripcion_proxima_diligencia || "",
      comentarios: detalle.comentarios || "",
      ...pruebasForm,
    }));

    setExistingDocs(Array.isArray(detalle.documentos) ? detalle.documentos : []);

    if (detalle.id_autoridad) {
      const au = autoridadesRaw.find(
        (a) => String(a.id_autoridad ?? a.id) === String(detalle.id_autoridad)
      );
      if (au) {
        setSelectedCiudadId(au.id_ciudad);
        const cObj = ciudadesById?.[String(au.id_ciudad)];
        if (cObj?.id_estado) setSelectedEstadoId(cObj.id_estado);
      }
      setSelectedAutoridadId(String(detalle.id_autoridad));
    }
  }, [isEdit, detalle, autoridadesRaw, ciudadesById]);

  const handleChangeEstado = (v) => {
    setSelectedEstadoId(v || null);
    setSelectedCiudadId(null);
    setSelectedAutoridadId(null);
  };
  const handleChangeCiudad = (v) => {
    setSelectedCiudadId(v || null);
    setSelectedAutoridadId(null);
  };

  const validate = () => {
    const e = {};
    if (!form.expediente.trim()) e.expediente = "Campo requerido";
    if (!safeArr(form.actores).some((a) => String(a || "").trim())) e.actores = "Campo requerido";
    if (!selectedAutoridadId) e.autoridad = "Selecciona una autoridad";
    if (!safeArr(form.demandados).some((d) => d.id_razon_social))
      e.demandados = "Selecciona al menos una razón social";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildPruebasDocumentacionPayload = () => {
    const actoraDetalle = { ...(form?.pruebas_detalle?.actora || {}) };

    const testimonialTestigos = Array.isArray(actoraDetalle?.testimonial?.testigos)
      ? actoraDetalle.testimonial.testigos
      : [];
    const documentalPublicaDocs = Array.isArray(actoraDetalle?.documental_publica?.documentos)
      ? actoraDetalle.documental_publica.documentos
      : [];
    const documentalPrivadaDocs = Array.isArray(actoraDetalle?.documental_privada?.documentos)
      ? actoraDetalle.documental_privada.documentos
      : [];
    const informeAutoridadDocs = Array.isArray(actoraDetalle?.informe_autoridad?.documentos)
      ? actoraDetalle.informe_autoridad.documentos
      : [];
    const pericialRegistros = Array.isArray(actoraDetalle?.pericial)
      ? actoraDetalle.pericial
      : Array.isArray(actoraDetalle?.pericial?.registros)
      ? actoraDetalle.pericial.registros
      : [];
    const inspeccionOcularRegistros = Array.isArray(actoraDetalle?.inspeccion_ocular)
      ? actoraDetalle.inspeccion_ocular
      : Array.isArray(actoraDetalle?.inspeccion_ocular?.registros)
      ? actoraDetalle.inspeccion_ocular.registros
      : [];

    if (actoraDetalle?.testimonial) {
      actoraDetalle.testimonial = {
        testigos: testimonialTestigos.map((t) => ({
          nombre: t?.nombre || "",
          relacion: t?.relacion || "",
          contacto: t?.contacto || "",
        })),
      };
    }

    if (actoraDetalle?.documental_publica) {
      actoraDetalle.documental_publica = {
        documentos: documentalPublicaDocs.map((d) => ({
          nombre: d?.nombre || "",
          descripcion: d?.descripcion || "",
          fecha: d?.fecha || null,
        })),
      };
    }

    if (actoraDetalle?.documental_privada) {
      actoraDetalle.documental_privada = {
        documentos: documentalPrivadaDocs.map((d) => ({
          nombre: d?.nombre || "",
          descripcion: d?.descripcion || "",
          fecha: d?.fecha || null,
        })),
      };
    }

    if (actoraDetalle?.informe_autoridad) {
      actoraDetalle.informe_autoridad = {
        documentos: informeAutoridadDocs.map((d) => ({
          nombre: d?.nombre || "",
          descripcion: d?.descripcion || "",
          fecha: d?.fecha || null,
        })),
      };
    }

    if (actoraDetalle?.pericial) {
      actoraDetalle.pericial = pericialRegistros.map((r) => ({
        tipo_pericial: Array.isArray(r?.tipo_pericial)
          ? r.tipo_pericial
          : r?.tipo_pericial
          ? [r.tipo_pericial]
          : [],
        nombre_perito: r?.nombre_perito || "",
        objeto_prueba: r?.objeto_prueba || "",
      }));
    }

    if (actoraDetalle?.inspeccion_ocular) {
      actoraDetalle.inspeccion_ocular = inspeccionOcularRegistros.map((r) => ({
        tipo_inspeccion_ocular: Array.isArray(r?.tipo_inspeccion_ocular)
          ? r.tipo_inspeccion_ocular
          : r?.tipo_inspeccion_ocular
          ? [r.tipo_inspeccion_ocular]
          : [],
        nombre_inspeccion_ocular: r?.nombre_inspeccion_ocular || "",
        objeto_prueba: r?.objeto_prueba || "",
      }));
    }

    return {
      tab: "actora",
      checklist: { actora: { ...(form?.pruebas_checklist?.actora || {}) } },
      detalle: { actora: actoraDetalle },
    };
  };

  const handleDeleteExistingDoc = async (doc) => {
    const docId = doc.id_junta_documento ?? doc.id;
    if (!docId) return;
    try {
      setDeletingDocId(docId);
      await dispatch(actionJuntaDeleteDocumento(idJunta, docId));
      setExistingDocs((prev) => prev.filter((d) => (d.id_junta_documento ?? d.id) !== docId));
      notify.success({ message: "Documento eliminado", placement: "bottomRight" });
    } catch (e) {
      notify.error({ message: "No se pudo eliminar el documento", placement: "bottomRight" });
    } finally {
      setDeletingDocId(null);
    }
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      setSubmitting(true);

      const actoresLimpios = safeArr(form.actores).map((a) => collapseSpaces(a)).filter(Boolean);
      const demandadosLimpios = safeArr(form.demandados)
        .filter((d) => d.id_razon_social)
        .map((d) => ({ id_razon_social: Number(d.id_razon_social), caracter: d.caracter || null }));

      const fechaAudiencia =
        form.fecha_audiencia_fecha
          ? `${form.fecha_audiencia_fecha} ${form.fecha_audiencia_hora || "00:00"}:00`
          : null;

      const payload = {
        expediente: form.expediente.trim(),
        nombre_actora: actoresLimpios.join(" - "),
        id_autoridad: selectedAutoridadId ? Number(selectedAutoridadId) : null,
        razones_sociales_ids: demandadosLimpios,
        corresponsal_nombre: form.corresponsal_nombre.trim() || null,
        abogado: form.abogado.trim() || null,
        competencia: form.competencia || null,
        tipo_procedimiento: form.tipo_procedimiento || null,
        regimen_reforma_2012: form.regimen_reforma_2012 || null,
        expedientes_relacionados: form.expedientes_relacionados.trim() || null,
        tipo_asesoria_actora: form.tipo_asesoria_actora || null,
        id_abogado_responsable: form.id_abogado_responsable ? Number(form.id_abogado_responsable) : null,
        etapa_procesal_inicial: form.etapa_procesal_inicial || null,
        accion_intentada: form.accion_intentada || null,
        fecha_presentacion_demanda: form.fecha_presentacion_demanda || null,
        fecha_recepcion: form.fecha_recepcion || null,
        fecha_emplazamiento: form.fecha_emplazamiento || null,
        fecha_audiencia: fechaAudiencia,
        fecha_ultima_actuacion: form.fecha_ultima_actuacion || null,
        fecha_proxima_diligencia: form.fecha_proxima_diligencia || null,
        descripcion_proxima_diligencia: form.descripcion_proxima_diligencia || null,
        comentarios: form.comentarios.trim() || null,
        pruebas_documentacion: buildPruebasDocumentacionPayload(),
      };

      const filesMap = pendingDocs.length ? { documentos: pendingDocs } : {};

      if (isEdit) {
        await dispatch(actionJuntaUpdateById(idJunta, payload, filesMap));
        notify.success({ message: "Actualizado correctamente", placement: "bottomRight" });
        navigate(`/materias/laboral/junta-conciliacion/${idJunta}`);
      } else {
        const res = await dispatch(actionJuntaCreate(payload, filesMap));
        const newId = res?.id || res?.data?.id || res?.id_junta;
        notify.success({ message: "Expediente creado", placement: "bottomRight" });
        navigate(
          newId
            ? `/materias/laboral/junta-conciliacion/${newId}`
            : "/materias/laboral/junta-conciliacion"
        );
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Error al guardar";
      notify.error({ message: "No se pudo guardar", description: msg, placement: "bottomRight" });
    } finally {
      setSubmitting(false);
    }
  };

  const ready = !isEdit || !!detalle;

  // Aviso informativo: si la demanda se presenta después de la entrada en
  // vigor del nuevo sistema (Tribunales) en la entidad seleccionada, el
  // asunto ya no corresponde a la Junta. Solo informativo, no bloquea el guardado.
  const regimenWarning = useMemo(() => {
    const estadoLabel = (estadosOptions || []).find(
      (o) => String(o.value) === String(selectedEstadoId)
    )?.label;
    const corte = estadoLabel && CORTE_REGIMEN[estadoLabel];
    if (corte && form.fecha_presentacion_demanda && form.fecha_presentacion_demanda >= corte) {
      return `La demanda se presentó después del ${corte}, fecha de entrada en vigor del nuevo sistema en ${estadoLabel}: el asunto corresponde al Tribunal Laboral, no a la Junta.`;
    }
    return "";
  }, [estadosOptions, selectedEstadoId, form.fecha_presentacion_demanda]);

  return (
    <HappyProvider>
    <div className="oa-exp-page">
      {/* Estilo local para las tarjetas "Partes y representación" — misma
          receta visual que .cc-section-card / .cc-body-grid de Centro de
          Conciliación (FormCentroConciliacion.jsx), con prefijo jc- para no
          chocar con nada más. */}
      <style>{`
        .jc-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; align-items: stretch; margin-bottom: 24px; }
        @media (max-width: 768px) { .jc-cols { grid-template-columns: 1fr; } }
        .jc-card { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 20px 24px; box-shadow: 0 2px 8px rgba(15,34,58,0.05); height: 100%; display: flex; flex-direction: column; }
        .jc-card-header { display: flex; align-items: center; gap: 10px; margin-bottom: 18px; font-size: 12px; font-weight: 800; letter-spacing: 0.09em; color: #111827; text-transform: uppercase; }
        .jc-card-icon { width: 28px; height: 28px; min-width: 28px; border-radius: 8px; background: #DCE9F5; display: flex; align-items: center; justify-content: center; font-size: 13px; color: #0A539A; }
        .jc-badge { margin-left: 6px; background: #0A539A; color: #fff; border-radius: 999px; font-size: 11px; font-weight: 700; padding: 1px 9px; min-width: 24px; text-align: center; text-transform: none; letter-spacing: normal; }
        .jc-rows { display: flex; flex-direction: column; gap: 8px; flex: 1; }
        .jc-row { display: flex; gap: 8px; align-items: flex-start; }
        .jc-row-body { flex: 1; min-width: 0; display: flex; flex-direction: row; gap: 8px; }
        .jc-row-body > * { flex: 1; min-width: 0; }
        @media (max-width: 560px) { .jc-row-body { flex-direction: column; } }
        .jc-remove-btn { border: 1px solid #e2e8f0; background: #fff; border-radius: 10px; padding: 0 14px; height: 40px; color: #ef4444; font-size: 13px; font-weight: 600; cursor: pointer; white-space: nowrap; }
        .jc-remove-btn:disabled { opacity: .35; cursor: not-allowed; }
        .jc-add-btn { margin-top: 8px; width: 100%; height: 40px; border-radius: 10px; border: 1px solid #e2e8f0; background: #fff; color: #0A539A; font-weight: 600; font-size: 13px; cursor: pointer; }
        .jc-add-btn:hover { background: #f8fafc; }
      `}</style>
      {/* ── TOPBAR ─────────────────────────────────────────────────── */}
      <section className="oa-exp-topbar">
        <div className="oa-exp-topbar-left">
          <div className="oa-exp-title">
            {isEdit ? "Editando Expediente de Junta de Conciliación" : "Creando Expediente de Junta de Conciliación"}
          </div>
          <div className="oa-exp-subtitle">Junta de Conciliación Laboral</div>
        </div>
        <div className="oa-exp-topbar-right">
          <Button
            className="oa-exp-btn oa-exp-btn-ghost"
            onClick={() =>
              navigate(
                isEdit
                  ? `/materias/laboral/junta-conciliacion/${idJunta}`
                  : "/materias/laboral/junta-conciliacion"
              )
            }
          >
            Cancelar
          </Button>
          <Button
            type="primary"
            className="oa-exp-btn"
            disabled={submitting}
            loading={submitting}
            onClick={handleSubmit}
            icon={
              <svg viewBox="0 0 448 512" width="13" height="13" fill="currentColor" aria-hidden="true">
                <path d="M64 0C28.7 0 0 28.7 0 64V448c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V154.5c0-17-6.7-33.3-18.7-45.3L400 60.7C388 48.7 371.7 42 354.5 42H64zm0 96c0-17.7 14.3-32 32-32H288c17.7 0 32 14.3 32 32v64c0 17.7-14.3 32-32 32H96c-17.7 0-32-14.3-32-32V96zM224 288a64 64 0 1 1 0 128 64 64 0 1 1 0-128z" />
              </svg>
            }
          >
            {isEdit ? "Guardar cambios" : "Guardar expediente"}
          </Button>
        </div>
      </section>

      {/* ── BODY ───────────────────────────────────────────────────── */}
      <main className="oa-exp-main" style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        {!ready ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : (
          <section className="oa-card">
            <div className="oa-card-head">
              <h2 className="oa-card-title">
                <span className="oa-card-icon">
                  <i className="fa-solid fa-folder-open" />
                </span>
                DATOS GENERALES DEL EXPEDIENTE
              </h2>
            </div>

            <div className="oa-card-body oa-grid-3">
              {/* NÚMERO DE EXPEDIENTE */}
              <div className="oa-field">
                <label className="oa-label">
                  <span className="oa-req">*</span> Número de Expediente
                </label>
                <input
                  className={`oa-input ${errors.expediente ? "oa-input-error" : ""}`}
                  type="text"
                  placeholder="Ej. 123/2024"
                  maxLength={25}
                  value={form.expediente}
                  onChange={(e) => setField("expediente", e.target.value)}
                />
                {errors.expediente && <div className="oa-error">{errors.expediente}</div>}
              </div>

              {/* ESTADO */}
              <div className="oa-field">
                <label className="oa-label">
                  <span className="oa-req">*</span> Estado
                </label>
                <select
                  className="oa-input"
                  value={selectedEstadoId ?? ""}
                  onChange={(e) => handleChangeEstado(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">Seleccionar estado</option>
                  {(estadosOptions || []).map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              {/* CIUDAD */}
              <div className="oa-field">
                <label className="oa-label">
                  <span className="oa-req">*</span> Ciudad
                </label>
                <select
                  className="oa-input"
                  value={selectedCiudadId ?? ""}
                  onChange={(e) => handleChangeCiudad(e.target.value ? Number(e.target.value) : null)}
                  disabled={!selectedEstadoId}
                >
                  <option value="">Seleccionar ciudad</option>
                  {ciudadesOptionsFinal.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              {/* AUTORIDAD */}
              <div className="oa-field">
                <label className="oa-label">
                  <span className="oa-req">*</span> Autoridad
                </label>
                <select
                  className={`oa-input ${errors.autoridad ? "oa-input-error" : ""}`}
                  value={selectedAutoridadId ?? ""}
                  onChange={(e) => {
                    setSelectedAutoridadId(e.target.value || null);
                    if (errors.autoridad)
                      setErrors((er) => { const c = { ...er }; delete c.autoridad; return c; });
                  }}
                  disabled={!selectedCiudadId}
                >
                  <option value="">Seleccionar autoridad</option>
                  {autoridadOptions.map((o) => (
                    <option key={String(o.value)} value={String(o.value)}>{o.label}</option>
                  ))}
                </select>
                {errors.autoridad && <div className="oa-error">{errors.autoridad}</div>}
              </div>

              {/* COMPETENCIA */}
              <div className="oa-field">
                <label className="oa-label">Competencia</label>
                <select
                  className="oa-input"
                  value={form.competencia || ""}
                  onChange={(e) => setField("competencia", e.target.value)}
                >
                  <option value="">Seleccionar competencia</option>
                  {COMPETENCIA_OPTS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              {/* TIPO DE PROCEDIMIENTO */}
              <div className="oa-field">
                <label className="oa-label">Tipo de Procedimiento</label>
                <select
                  className="oa-input"
                  value={form.tipo_procedimiento || ""}
                  onChange={(e) => setField("tipo_procedimiento", e.target.value)}
                >
                  <option value="">Seleccionar procedimiento</option>
                  {TIPO_PROCEDIMIENTO_OPTS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              {/* EXPEDIENTE(S) ACUMULADO(S) O RELACIONADO(S) */}
              <div className="oa-field">
                <label className="oa-label">Expediente(s) Acumulado(s) o Relacionado(s)</label>
                <input
                  className="oa-input"
                  type="text"
                  placeholder="Ej. 0457/2019, 0461/2019"
                  maxLength={255}
                  value={form.expedientes_relacionados}
                  onChange={(e) => setField("expedientes_relacionados", e.target.value)}
                />
              </div>

              {/* RÉGIMEN (REFORMA LABORAL 2010) */}
              <div className="oa-field">
                <label className="oa-label">Régimen (Reforma Laboral 2012)</label>
                <select
                  className="oa-input"
                  value={form.regimen_reforma_2012 || ""}
                  onChange={(e) => setField("regimen_reforma_2012", e.target.value)}
                >
                  <option value="">Seleccionar régimen</option>
                  {REGIMEN_REFORMA_2012_OPTS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </section>
        )}

        {ready && (
          <div className="jc-cols">
            {/* ACTORES (repetible) */}
            <div className="jc-card">
              <div className="jc-card-header">
                <span className="jc-card-icon">
                  <i className="fa-solid fa-user" />
                </span>
                Nombre de la Parte Actora
                <span className="jc-badge">{form.actores.length}</span>
              </div>

              <div className="jc-rows">
                {form.actores.map((actor, idx) => (
                  <div key={idx} className="jc-row">
                    <input
                      className={`oa-input ${errors.actores ? "oa-input-error" : ""}`}
                      type="text"
                      placeholder={`Actor ${idx + 1} (nombre completo)`}
                      maxLength={150}
                      value={actor}
                      onChange={(e) => setActorAt(idx, e.target.value)}
                    />
                    <button
                      type="button"
                      className="jc-remove-btn"
                      disabled={form.actores.length <= 1}
                      onClick={() => removeActor(idx)}
                    >
                      Quitar
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" className="jc-add-btn" onClick={addActor}>
                + Agregar otro actor
              </button>
              {errors.actores && <div className="oa-error">{errors.actores}</div>}
            </div>

            {/* DEMANDADOS (razón social + carácter, repetible) */}
            <div className="jc-card">
              <div className="jc-card-header">
                <span className="jc-card-icon">
                  <i className="fa-solid fa-building" />
                </span>
                Razón Social Demandada
                <span className="jc-badge">{form.demandados.length}</span>
              </div>

              <div className="jc-rows">
                {form.demandados.map((d, idx) => (
                  <div key={idx} className="jc-row">
                    <div className="jc-row-body">
                      <Select
                        value={d.id_razon_social || undefined}
                        placeholder="Seleccionar razón social"
                        onChange={(v) => setDemandadoAt(idx, { id_razon_social: v })}
                        options={todasRazonesOptions}
                        style={{ width: "100%" }}
                        optionFilterProp="label"
                        showSearch
                        status={errors.demandados ? "error" : ""}
                      />
                      <Select
                        value={d.caracter || undefined}
                        placeholder="Carácter con que se le demanda"
                        onChange={(v) => setDemandadoAt(idx, { caracter: v })}
                        options={CARACTER_DEMANDADO_OPTS.map((c) => ({ label: c, value: c }))}
                        style={{ width: "100%" }}
                        allowClear
                      />
                    </div>
                    <button
                      type="button"
                      className="jc-remove-btn"
                      disabled={form.demandados.length <= 1}
                      onClick={() => removeDemandado(idx)}
                    >
                      Quitar
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" className="jc-add-btn" onClick={addDemandado}>
                + Agregar otro demandado
              </button>
              {errors.demandados && <div className="oa-error">{errors.demandados}</div>}
            </div>
          </div>
        )}

        {ready && (
          <section className="oa-card">
            <div className="oa-card-head">
              <h2 className="oa-card-title">
                <span className="oa-card-icon">
                  <i className="fa-solid fa-user-tie" />
                </span>
                REPRESENTACIÓN Y CONTROL INTERNO
              </h2>
            </div>

            <div className="oa-card-body">
              <div className="oa-grid-3">
                <div className="oa-field">
                  <label className="oa-label">Abogado de la Parte Actora</label>
                  <input
                    className="oa-input"
                    type="text"
                    placeholder="Nombre del abogado"
                    maxLength={150}
                    value={form.abogado}
                    onChange={(e) => setField("abogado", e.target.value)}
                  />
                </div>

                <div className="oa-field">
                  <label className="oa-label">Tipo de Asesoría de la Actora</label>
                  <select
                    className="oa-input"
                    value={form.tipo_asesoria_actora || ""}
                    onChange={(e) => setField("tipo_asesoria_actora", e.target.value)}
                  >
                    <option value="">Seleccionar</option>
                    {TIPO_ASESORIA_OPTS.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>

                <div className="oa-field">
                  <label className="oa-label">Corresponsal</label>
                  <input
                    className="oa-input"
                    type="text"
                    placeholder="No hay corresponsal"
                    maxLength={150}
                    value={form.corresponsal_nombre}
                    onChange={(e) => setField("corresponsal_nombre", e.target.value)}
                  />
                </div>

                <div className="oa-field">
                  <label className="oa-label">Abogado Responsable del Despacho</label>
                  <Select
                    value={form.id_abogado_responsable || undefined}
                    placeholder="Seleccionar abogado"
                    onChange={(v) => setField("id_abogado_responsable", v || "")}
                    options={abogadosOptions}
                    style={{ width: "100%" }}
                    optionFilterProp="label"
                    showSearch
                    allowClear
                  />
                </div>
              </div>
            </div>
          </section>
        )}

        {ready && (
          <section className="oa-card">
            <div className="oa-card-head">
              <h2 className="oa-card-title">
                <span className="oa-card-icon">
                  <i className="fa-solid fa-timeline" />
                </span>
                ESTADO DEL EXPEDIENTE AL DARLO DE ALTA
              </h2>
            </div>

            <div className="oa-card-body">
              <div className="oa-grid-12">
                <div className="oa-field oa-col-4">
                  <label className="oa-label">
                    <span className="oa-req">*</span> Etapa Procesal Actual
                  </label>
                  <select
                    className="oa-input"
                    value={form.etapa_procesal_inicial || ""}
                    onChange={(e) => setField("etapa_procesal_inicial", e.target.value)}
                  >
                    <option value="">Seleccionar etapa</option>
                    {ETAPA_PROCESAL_OPTS.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>

                <div className="oa-field oa-col-8">
                  <label className="oa-label">
                    <span className="oa-req">*</span> Acción Intentada
                  </label>
                  <select
                    className="oa-input"
                    value={form.accion_intentada || ""}
                    onChange={(e) => setField("accion_intentada", e.target.value)}
                  >
                    <option value="">Seleccionar acción</option>
                    {ACCION_INTENTADA_OPTS.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>
              </div>

              <h3
                className="oa-mt-4"
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: "0.11em",
                  textTransform: "uppercase",
                  color: "#0f172a",
                  margin: "24px 0 16px",
                  paddingTop: 16,
                  borderTop: "1px solid #e2e8f0",
                }}
              >
                Fechas Mínimas de Control
              </h3>
              <div className="oa-grid-3">
                <div className="oa-field">
                  <label className="oa-label">Fecha de Presentación de la Demanda</label>
                  <ScrollDateInput
                    style={{ width: "100%" }}
                    value={form.fecha_presentacion_demanda}
                    onChange={(v) => setField("fecha_presentacion_demanda", v)}
                  />
                </div>
                <div className="oa-field">
                  <label className="oa-label">Fecha de Recepción en el Despacho</label>
                  <ScrollDateInput
                    style={{ width: "100%" }}
                    value={form.fecha_recepcion}
                    onChange={(v) => setField("fecha_recepcion", v)}
                  />
                </div>
                <div className="oa-field">
                  <label className="oa-label">Fecha de Emplazamiento</label>
                  <ScrollDateInput
                    style={{ width: "100%" }}
                    value={form.fecha_emplazamiento}
                    onChange={(v) => setField("fecha_emplazamiento", v)}
                  />
                </div>
                <div className="oa-field">
                  <label className="oa-label">Audiencia de Conciliación, Demanda y Excepciones</label>
                  <ScrollDateInput
                    style={{ width: "100%" }}
                    value={form.fecha_audiencia_fecha}
                    onChange={(v) => setField("fecha_audiencia_fecha", v)}
                  />
                </div>
                <div className="oa-field">
                  <label className="oa-label">Hora de la Audiencia</label>
                  <input
                    className="oa-input"
                    type="time"
                    value={form.fecha_audiencia_hora}
                    onChange={(e) => setField("fecha_audiencia_hora", e.target.value)}
                  />
                </div>
                <div className="oa-field">
                  <label className="oa-label">Fecha de la Última Promoción o Actuación</label>
                  <ScrollDateInput
                    style={{ width: "100%" }}
                    value={form.fecha_ultima_actuacion}
                    onChange={(v) => setField("fecha_ultima_actuacion", v)}
                  />
                </div>
                <div className="oa-field">
                  <label className="oa-label">Próxima Diligencia Programada</label>
                  <ScrollDateInput
                    style={{ width: "100%" }}
                    value={form.fecha_proxima_diligencia}
                    onChange={(v) => setField("fecha_proxima_diligencia", v)}
                  />
                </div>
                <div className="oa-field">
                  <label className="oa-label">Descripción de la Próxima Diligencia</label>
                  <select
                    className="oa-input"
                    value={form.descripcion_proxima_diligencia || ""}
                    onChange={(e) => setField("descripcion_proxima_diligencia", e.target.value)}
                  >
                    <option value="">Seleccionar diligencia</option>
                    {DILIGENCIA_OPTS.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="oa-field oa-mt-4">
                <label className="oa-label">Observaciones de la Etapa Actual</label>
                <textarea
                  className="oa-input oa-textarea"
                  placeholder="Estado real del expediente, acuerdos pendientes de notificar, exhortos girados…"
                  value={form.comentarios}
                  onChange={(e) => setField("comentarios", e.target.value)}
                />
              </div>

              {regimenWarning && (
                <div
                  className="oa-mt-4"
                  style={{
                    fontSize: 11.5,
                    lineHeight: 1.5,
                    color: "#d92d20",
                    background: "#fef3f2",
                    border: "1px solid #fecdc9",
                    borderRadius: 8,
                    padding: "10px 12px",
                  }}
                >
                  {regimenWarning}
                </div>
              )}
            </div>
          </section>
        )}

        {ready && (
          <section className="oa-card">
            <div className="oa-card-head">
              <h2 className="oa-card-title">
                <span className="oa-card-icon">
                  <i className="fa-solid fa-paperclip" />
                </span>
                DOCUMENTOS
              </h2>
            </div>

            <div className="oa-card-body">
              <p className="oa-pruebas-sub">
                Adjunta uno o varios documentos del expediente (PDF, Word o imágenes).
              </p>

              {!!existingDocs.length && (
                <div className="oa-testigos-stack" style={{ marginBottom: 16 }}>
                  {existingDocs.map((doc) => {
                    const docId = doc.id_junta_documento ?? doc.id;
                    const displayName =
                      doc.filename || (doc.path ? String(doc.path).split("/").pop() : "Documento");
                    return (
                      <div key={docId} className="oa-testigo-card" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                          <FileOutlined />
                          {doc.url ? (
                            <a href={`${PATH}${doc.url}`} target="_blank" rel="noreferrer" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {displayName}
                            </a>
                          ) : (
                            <span>{displayName}</span>
                          )}
                        </div>
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          loading={deletingDocId === docId}
                          onClick={() => handleDeleteExistingDoc(doc)}
                        >
                          Eliminar
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}

              <Upload.Dragger
                multiple
                fileList={pendingDocs}
                beforeUpload={(file) => {
                  const allowedTypes = [
                    "application/pdf",
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                    "application/msword",
                    "image/jpeg",
                    "image/png",
                    "image/webp",
                  ];
                  if (!allowedTypes.includes(file.type)) {
                    notify.error({
                      message: "Tipo de archivo no válido",
                      description: "Solo se permiten archivos PDF, Word o imágenes (JPG, PNG, WEBP).",
                    });
                    return Upload.LIST_IGNORE;
                  }
                  return false;
                }}
                onChange={({ fileList }) => setPendingDocs(fileList)}
                onRemove={(file) => setPendingDocs((prev) => prev.filter((f) => f.uid !== file.uid))}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text" style={{ fontWeight: 600, fontSize: 13 }}>
                  Haz clic o arrastra aquí los archivos
                </p>
                <p className="ant-upload-hint" style={{ fontSize: 11, marginBottom: 8 }}>
                  Puedes seleccionar varios documentos a la vez (PDF, Word o imágenes).
                </p>
              </Upload.Dragger>
            </div>
          </section>
        )}

      </main>
    </div>
    </HappyProvider>
  );
}
